<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useLoadInventTypesByObject } from '@/entities/Inventory/TypesByObject/model/api'
import { mapPassportPermissionElementTypes } from '@/features/Role/RoleEdit/model/roleObjectTypes'
import type { PassportPermission } from '@/entities/Role'
import type { PassportPermissionFlag } from '@/entities/Auth'
import { useTranslate } from '@/shared/lib'
import { VPreloader } from '@/shared/ui'
import './assets/style.scss'

const props = defineProps<{
  modelValue: PassportPermission[]
  groupId: number
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: PassportPermission[]): void
}>()

const show = defineModel<boolean>('show', { default: false })
const { trs } = useTranslate()

const objectId = computed(() => props.groupId)
const elementTypesQuery = useLoadInventTypesByObject(objectId)
const objectTypes = computed(() => {
  const rows = elementTypesQuery.data.value?.data
  return mapPassportPermissionElementTypes(Array.isArray(rows) ? rows : [])
})

const localPerms = ref<Record<number, PassportPermission>>({})

const isCustom = (typeId: number): boolean => !!localPerms.value[typeId]

const normalizePerm = (raw: PassportPermission & { detailed_view?: boolean }): PassportPermission => {
  if (raw.read !== undefined || raw.update !== undefined) {
    return {
      object_type_id: raw.object_type_id,
      read: Boolean(raw.read),
      update: Boolean(raw.update),
    }
  }
  const legacy = Boolean(raw.detailed_view)
  return {
    object_type_id: raw.object_type_id,
    read: legacy,
    update: legacy,
  }
}

const ensurePerm = (objectTypeId: number): PassportPermission => {
  if (!localPerms.value[objectTypeId]) {
    localPerms.value[objectTypeId] = {
      object_type_id: objectTypeId,
      read: true,
      update: true,
    }
  }
  return localPerms.value[objectTypeId]
}

const restrictType = (typeId: number) => {
  ensurePerm(typeId)
}

const resetType = (typeId: number) => {
  const copy = { ...localPerms.value }
  delete copy[typeId]
  localPerms.value = copy
}

const initLocalPerms = () => {
  const map: Record<number, PassportPermission> = {}
  for (const p of props.modelValue ?? []) {
    map[p.object_type_id] = normalizePerm(p as PassportPermission & { detailed_view?: boolean })
  }
  localPerms.value = map
}

watch(show, (visible) => {
  if (visible) initLocalPerms()
})

watch(
  () => props.modelValue,
  () => {
    if (show.value) initLocalPerms()
  },
)

watch(
  () => objectTypes.value,
  () => {
    if (show.value) initLocalPerms()
  },
)

const onReadChange = (typeId: number, value: boolean) => {
  const p = ensurePerm(typeId)
  p.read = value
  if (!value) {
    p.update = false
  }
}

const permissionFields: ReadonlyArray<{
  key: PassportPermissionFlag
  label: string
  master?: boolean
  default?: boolean
}> = [
  { key: 'read', label: 'Чтение', master: true, default: false },
  { key: 'update', label: 'Изменение', default: false },
]

const onPermChange = (typeId: number, key: PassportPermissionFlag, value: boolean) => {
  if (key === 'read') {
    onReadChange(typeId, value)
    return
  }
  ensurePerm(typeId)[key] = value
}

const save = () => {
  emit('update:modelValue', Object.values(localPerms.value))
  show.value = false
}
</script>

<template>
  <VDialog v-model="show" max-width="720">
    <VCard>
      <VCardTitle class="passport-detailed-view-permissions__title">
        <span>{{ trs('itf.passport_detailed_view_permission') }}</span>
      </VCardTitle>

      <VDivider />

      <VCardText class="passport-detailed-view-permissions__body">
        <VPreloader v-if="elementTypesQuery.isPending.value" :wrapper-height="120" />

        <VList v-else class="passport-detailed-view-permissions__list" lines="two">
          <template v-for="(t, index) in objectTypes" :key="t.catalogId">
            <VDivider v-if="index > 0" class="passport-detailed-view-permissions__divider" />

            <VListItem class="passport-detailed-view-permissions__item">
              <template #title>
                <span class="passport-detailed-view-permissions__name">{{ t.name }}</span>
              </template>

              <template #subtitle>
                <p
                  v-if="!isCustom(t.permissionTypeId)"
                  class="passport-detailed-view-permissions__status passport-detailed-view-permissions__status_full"
                >
                  {{ trs('itf.passport_detailed_view_permission_full_access') }}
                </p>
                <p
                  v-else
                  class="passport-detailed-view-permissions__status passport-detailed-view-permissions__status_restricted"
                >
                  {{ trs('itf.passport_detailed_view_permission_restricted') }}
                </p>
              </template>

              <template #append>
                <VBtn
                  v-if="!isCustom(t.permissionTypeId)"
                  size="small"
                  variant="tonal"
                  color="primary"
                  prepend-icon="edit"
                  @click="restrictType(t.permissionTypeId)"
                >
                  {{ trs('itf.passport_detailed_view_permission_restrict') }}
                </VBtn>
                <VBtn
                  v-else
                  size="small"
                  variant="text"
                  color="error"
                  prepend-icon="restart_alt"
                  @click="resetType(t.permissionTypeId)"
                >
                  {{ trs('itf.passport_detailed_view_permission_reset') }}
                </VBtn>
              </template>
            </VListItem>

            <div
              v-if="isCustom(t.permissionTypeId)"
              class="passport-detailed-view-permissions__perms-wrap"
            >
              <div class="passport-detailed-view-permissions__perms">
                <VCheckbox
                  v-for="field in permissionFields"
                  :key="field.key"
                  :model-value="localPerms[t.permissionTypeId]?.[field.key] ?? field.default"
                  :label="field.label"
                  color="primary"
                  density="compact"
                  hide-details
                  :disabled="!field.master && !localPerms[t.permissionTypeId]?.read"
                  @update:model-value="(v) => onPermChange(t.permissionTypeId, field.key, Boolean(v))"
                />
              </div>
            </div>
          </template>
        </VList>

        <div v-if="!elementTypesQuery.isPending.value && !objectTypes.length" class="passport-detailed-view-permissions__empty">
          <VIcon icon="folder_open" size="48" />
          {{ trs('itf.passport_detailed_view_permission_empty') }}
        </div>
      </VCardText>

      <VDivider />

      <VCardActions class="passport-detailed-view-permissions__actions">
        <VBtn color="default" variant="tonal" @click="show = false">{{ trs('itf.cancel') }}</VBtn>
        <VBtn color="primary" variant="flat" @click="save">{{ trs('itf.save') }}</VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
